import closeBillingModal from "@common-assets/icons/closeBillingModal.png";
import { User, userDisplayName } from "@common/models/user";
import { useSelector } from "react-redux";
import classes from "../account-view/account-view.module.scss";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import BTButton from "@common-components/bt-button/bt-button";
import BTDrawer from "@common-components/bt-drawer/bt-drawer";
import EditContactDetailsForm, { countryList } from "@common-components/edit-contact-details-form/edit-contact-details-form";
import InvoiceTable from "@common-components/invoice-table/invoice-table";
import ReusableModal from "@common-components/modals/reusable-modal";
import { getCreditCardLogo } from "@common-components/payment-form/payment-form";
import TrialProgressBar from "@common-components/trial-progress-bar/trial-progress-bar";
import { PlanDescriptor } from "@common-config/app.config";
import { namespace } from "@common-config/constants";
import { ShopperInfo } from "@common-models/shopper-info";
import { useGetShopperInfoMutation, useGetWorkspaceInvoicesMutation, useUpdateShopperInfoMutation } from "@common-reducers/backend-api/backend-api";
import { DBSubscriptionThunks } from "@common-reducers/DBServiceThunks";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { useRouter } from "@common-reducers/hooks/use-router";
import { MODAL, modalActions } from "@common-reducers/ModalReducer";
import { paymentFormActions } from "@common-reducers/PaymentFormReducer";
import { openPaymentDialogThunk } from "@common-reducers/PaymentFormThunks";
import { activeSubscriptionSelector, latestSubscriptionSelector, selectUserObject } from "@common-reducers/UserSelectors";
import { selectBillingViewMode } from "./billing-view-mode";
import { cancelSubscriptionThunk, updateChargeDateThunk } from "@common-reducers/UserThunks";
import { modifyPlanName } from "@common-services/utils";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import moment from "moment";
import pluralize from "pluralize";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface AdminBillingViewProps {
  readOnly?: boolean;

}

export interface AdminBillingViewState {
  user?: User | null | undefined;
  loading: boolean;
}

interface BillingPaymentMethod {
  cardType: string;
  last4digits: string;
  isDefault?: boolean;
  isBackup?: boolean;
}

const AdminBillingView = ({ readOnly }: AdminBillingViewProps) => {
  const { t } = useTranslation(namespace);
  const router = useRouter();
  const user = useSelector(selectUserObject);
  const workspaceSubscription = useAppSelector(activeSubscriptionSelector) ?? null;
  const latestSubscription = useAppSelector(latestSubscriptionSelector) ?? null;
  const [getWorkspaceInvoices, { data: workspaceInvoices }] = useGetWorkspaceInvoicesMutation();
  const [getShopperInfo, { data: shopper, isLoading: getShopperInfoLoading }] = useGetShopperInfoMutation();
  const [updateShopperInfo, { isLoading: updateShopperInfoLoading }] = useUpdateShopperInfoMutation();
  const creditCardDetailsUpdated = useAppSelector(state => state.PaymentFormReducer.creditCardDetailsUpdated)
  const [subscriptionCancelling, setSubscriptionCancelling] = useState(false);
  const [drawerStatus, setDrawerStatus] = useState<boolean>(false);
  const [drawerView, setCurrentDrawerView] = useState<string>();
  const [nextChargeDate, setNextChargeDate] = useState('');
  const today = new Date().toISOString().split('T')[0];
  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNextChargeDate(event.target.value);
  };

  const [invoice] = workspaceInvoices?.invoiceList ?? [];

  const displaySubscription = workspaceSubscription || latestSubscription;
  const plan: PlanDescriptor = displaySubscription?.plan as PlanDescriptor;

  const billingType = useMemo(() => plan?.intervalUnit === 'month' ? t('adminBillingView.motnhly') : t('adminBillingView.yearly'), [plan?.intervalUnit, t])
  const dispatch = useAppDispatch()

  const isLoading = getShopperInfoLoading || updateShopperInfoLoading;

  useEffect(() => {
    dispatch(DBSubscriptionThunks.find({}));
    getWorkspaceInvoices();
    if (displaySubscription?.subscription_id) {
      getShopperInfo({ subscriptionId: displaySubscription.subscription_id });
    }
  }, [displaySubscription?.subscription_id])

  useEffect(() => {
    if (creditCardDetailsUpdated) {
      if (displaySubscription?.subscription_id) {
        getShopperInfo({ subscriptionId: displaySubscription.subscription_id });
      }
      dispatch(paymentFormActions.setCreditCardDetailsUpdated(false));

    }
  }, [creditCardDetailsUpdated, dispatch, getShopperInfo, displaySubscription?.subscription_id])

  //const creditCard = invoice?.["financial-transactions"]?.["financial-transaction"]?.["credit-card"] ?? {};
  const creditCard = shopper?.subscription?.["credit-card"]

  //const cardType = creditCard["card-type"] ?? invoice?.["financial-transactions"]?.["financial-transaction"]?.["payment-method"];
  const cardType = creditCard?.["card-type"] ?? ((workspaceSubscription?.getType() === 'paypal' || shopper?.subscription?.['paypal-subscription']) ? 'PayPal' : undefined);
  const creditCardLogo = useMemo(() => cardType ? getCreditCardLogo(cardType) : undefined, [cardType]);

  // FE#135: pick the billing view from the CURRENT workspace's active subscription — never let a
  // dead/cancelled relic (latestSubscription) masquerade as the current subscription. See billing-view-mode.ts.
  const billingViewMode = selectBillingViewMode({
    hasActiveSubscription: !!workspaceSubscription,
    activePlanId: workspaceSubscription?.plan_id,
    latestPlanId: latestSubscription?.plan_id,
  });

  const shopperContactInfo = useMemo(() => {
    return shopper?.shopper?.["shopper-info"]?.["shopper-contact-info"];
  }, [shopper]);

  const firstName = shopperContactInfo?.['first-name'];
  const lastName = shopperContactInfo?.['last-name'];
  const fullName = [firstName, lastName].filter(x => x).join(' ') || userDisplayName(user ?? {});
  const address1 = shopperContactInfo?.address1;
  const address2 = shopperContactInfo?.address2;
  const zip = shopperContactInfo?.zip?.toString();
  const countryCode = user?.invoiceDetails?.country || shopperContactInfo?.country;
  const countryObject = countryList.find(country => country.isoCode.toLowerCase() === countryCode?.toLowerCase());
  const companyName = shopperContactInfo?.['company-name'];
  const vatId = user?.invoiceDetails?.vatId;

  const fullAddress = [address1, zip, countryObject?.name].filter(x => x && x !== '--').join(', ');
  const invoiceDetails: ShopperInfo = {
    firstName,
    lastName,
    companyName,
    address1,
    address2,
    zip,
    vatId,
    country: countryObject?.isoCode
  }

  const planName = shopper?.planName;

  const toggleDrawer = (value: string) => {
    setCurrentDrawerView(value)
    setDrawerStatus(!drawerStatus);
  };

  const handleDateChangeSubmit = async () => {
    if (!displaySubscription?.subscription_id) return;
    const formattedDate = moment(nextChargeDate).format('DD-MMM-YY');
    let res = await dispatch(updateChargeDateThunk({
      subscriptionId: displaySubscription.subscription_id,
      nextChargeDate: formattedDate
    }));
    if (res) {
      getWorkspaceInvoices();
      if (displaySubscription?.subscription_id) {
        getShopperInfo({ subscriptionId: displaySubscription.subscription_id });
      }
      await dispatch(DBSubscriptionThunks.find({}));
    }

    // if ((res.payload as { status?: number })?.status === 204){
    //   alert('Date updated successfully')
    // }
    // if ((res.payload as { error?: string })?.error){
    //   alert('Error updating date')
    // }
  }

  const callCancelSubscription = useCallback(async () => {
    if (!displaySubscription?._id) return;
    setSubscriptionCancelling(true);
    await dispatch(cancelSubscriptionThunk({
      subscriptionID: displaySubscription._id,
      reason: 'Cancelled on account page'
    }));
    setSubscriptionCancelling(false);
  }, [dispatch, displaySubscription?._id])

  const handleSubmit = async (shopperInfo: ShopperInfo) => {
    toggleDrawer("");
    await updateShopperInfo({
      shopperInfo
    });
    if (displaySubscription?.subscription_id) {
      await getShopperInfo({ subscriptionId: displaySubscription.subscription_id });
    }
  };


  const paymentInfoBox = () => (
    <>
      {invoice && (
        <Card>
          <CardHeader>
            <CardTitle>{t('adminBillingView.paymentMethod')}</CardTitle>
          </CardHeader>
          <CardContent>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="flex items-center justify-center p-3 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
                  <img alt='' src={creditCardLogo} className="h-8 w-auto" />
                </div>
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('adminBillingView.paymentMethod')}
                  </div>
                  {creditCard && (
                    <div className="flex items-center gap-2 text-lg font-semibold text-blue-600 dark:text-blue-400">
                      <span>{cardType}</span>
                      {cardType !== 'PayPal' && (
                        <span className="font-normal text-gray-600 dark:text-gray-400">
                          {`${t('adminBillingView.endingWith')} ${(creditCard["card-last-four-digits"] ?? '').toString().padStart(4, '0')}`}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {cardType !== 'PayPal' && (
                <Button
                  disabled={readOnly}
                  variant="outline"
                  onClick={() => {
                    dispatch(openPaymentDialogThunk({}))
                  }}
                  className="text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  {t('adminBillingView.changePaymentMethod')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card >
      )}
    </>
  )

  useEffect(() => {
    document.title = t('adminBillingView.documentTitle');
  }, [t]);

  const getTrialDaysLeft = () => {
    if (!displaySubscription) return 0;
    const expiredDate = moment(displaySubscription.expiredDate);
    const currentDate = moment();
    const daysLeft = expiredDate.diff(currentDate, 'days');
    return daysLeft
  }


  const buildBillingView = () =>
    <div className="space-y-6 max-w-6xl mx-auto p-6">
      {(user?.role === "support" || user?.role === "admin") &&
        <Card className="border-yellow-200 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20">
          <CardHeader>
            <CardTitle className="text-lg text-yellow-800 dark:text-yellow-300">{t('adminBillingView.supportActions')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {t('adminBillingView.currentSubscriptionId')}: {displaySubscription?.subscription_id}
            </div>
              <div className="flex flex-col space-y-3">
              <Button
                variant="outline"
                onClick={() => dispatch(modalActions.setComponentToRender(MODAL.UPDATE_SUBSCRIPTION_WORKSPACE))}
                className="w-fit text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                {t('adminBillingView.transferSubscription')}
              </Button>
              <div className="flex flex-col space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('adminBillingView.updateNextChargeDate')}</label>
                <Input
                  type="date"
                  value={nextChargeDate}
                  onChange={handleDateChange}
                  min={today}
                  className="w-fit dark:text-gray-100 dark:[color-scheme:dark]"
                />
                <Button onClick={handleDateChangeSubmit} className="w-fit bg-blue-600 hover:bg-blue-700 text-white">
                  {t('common.submit')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      }
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {t('adminBillingView.currentSubscription')}
            {displaySubscription?.isActive ? (
              <Alert className="w-fit px-3 py-2 bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700 text-green-800 dark:text-green-200">
                <CheckCircle className="w-4 h-4 text-green-700 dark:text-green-400" />
                <AlertDescription className="!text-green-800 dark:!text-green-200 font-semibold ml-2">{t('adminBillingView.active')}</AlertDescription>
              </Alert>
            ) : (
              <Alert className="w-fit px-3 py-2 bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700 !text-red-800 dark:!text-red-200" variant="destructive">
                <XCircle className="w-4 h-4 text-red-700 dark:text-red-400" />
                <AlertDescription className="!text-red-800 dark:!text-red-200 font-semibold ml-2">{t('adminBillingView.expired')}</AlertDescription>
              </Alert>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('adminBillingView.currentPlan')}</div>
              <div className="text-lg font-semibold text-blue-600 dark:text-blue-400" data-testid={'admin-billing-view-current-plan'}>
                {modifyPlanName(planName)}
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('adminBillingView.teamSize')}</div>
              <div className="text-lg font-semibold text-blue-600 dark:text-blue-400" data-testid={'admin-billing-view-team-size'}>
                {pluralize('member', plan?.seatCount ?? 1, true)}
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('adminBillingView.billingPeriod')}</div>
              <div className="text-lg font-semibold text-blue-600 dark:text-blue-400" data-testid={'admin-billing-view-billing-period'}>
                {billingType}
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('adminBillingView.amount')}</div>
              <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                ${plan?.price}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
            <Button
              disabled={readOnly}
              variant="outline"
              onClick={() => router.navigate('/account-subscription', router.query)}
              className="w-full text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              {t('adminBillingView.changePlan')}
            </Button>
            <Button
              disabled={readOnly}
              variant="outline"
              onClick={() => router.navigate('/account-subscription', router.query)}
              className="w-full text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              {t('adminBillingView.editTeamSize')}
            </Button>
            <div className="w-full">
              {displaySubscription?.isLive ? (
                <BTButton
                  loading={subscriptionCancelling}
                  onClick={() => {
                    callCancelSubscription()
                  }}
                  btvariant="text"
                >
                  {t('adminBillingView.cancelSubscription')}
                </BTButton>
              ) : (
                <Alert className="bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700 !text-red-800 dark:!text-red-200" variant="destructive">
                  <XCircle className="w-4 h-4 text-red-700 dark:text-red-400" />
                  <AlertDescription className="!text-red-800 dark:!text-red-200 font-medium ml-2">{t('adminBillingView.cancelled')}</AlertDescription>
                </Alert>
              )}
            </div>
            <div className="w-full">
              {displaySubscription?.isActive ? (
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {displaySubscription?.isLive ? t('adminBillingView.renewalTime') : t('adminBillingView.expires')}:
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {workspaceSubscription?.renewalTime?.format('MMM DD, YYYY')}
                  </div>
                </div>
              ) : (
                <Alert className="bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700 !text-red-800 dark:!text-red-200" variant="destructive">
                  <XCircle className="w-4 h-4 text-red-700 dark:text-red-400" />
                  <AlertDescription className="!text-red-800 dark:!text-red-200 font-medium ml-2">Expired</AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      {paymentInfoBox()}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('adminBillingView.invoices')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400">
                {t('adminBillingView.viewDownloadInvoices')}
              </p>
              <BTButton
                btvariant="secondary"
                onClick={() => {
                  toggleDrawer("Invoices");
                }}
              >
                {t('adminBillingView.viewInvoices')}
              </BTButton>
            </div>
          </CardContent>
        </Card>

        {shopperContactInfo && (
          <Card>
            <CardHeader>
              <CardTitle>{t('adminBillingView.invoiceDetails')}</CardTitle>
            </CardHeader>
            <CardContent>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('adminBillingView.companyName')}</div>
                    <div className="text-gray-900 dark:text-gray-100">{companyName || t('adminBillingView.notSpecified')}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('adminBillingView.fullName')}</div>
                    <div className="text-gray-900 dark:text-gray-100">{fullName}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('adminBillingView.address')}</div>
                    <div className="text-gray-900 dark:text-gray-100">{fullAddress || t('adminBillingView.notSpecified')}</div>
                  </div>
                </div>
                <BTButton
                  disabled={readOnly}
                  btvariant="secondary"
                  onClick={() => {
                    toggleDrawer("Invoice details")
                  }}
                >
                  {t('adminBillingView.editInvoiceDetails')}
                </BTButton>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>

  const buildNoSubscriptionView = () => (
    <div className="space-y-6 max-w-4xl mx-auto p-6">
      <Card>
        <CardContent className="text-center py-12">
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {t('adminBillingView.noActiveSubscription')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
              {t('adminBillingView.noSubscription')}
            </p>
            <BTButton onClick={() => {
              router.navigate('/account-subscription', router.query)
            }}>
              {t('adminBillingView.upgrade')}
            </BTButton>
          </div>
        </CardContent>
      </Card>
    </div>
  )

  const buildTrialView = () => (
    <div className="space-y-6 max-w-6xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {t('adminBillingView.trialSubscription')}
            {getTrialDaysLeft() === 0 ? (
              <Alert className="w-fit px-3 py-2 bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700 !text-red-800 dark:!text-red-200" variant="destructive">
                <XCircle className="w-4 h-4 text-red-700 dark:text-red-400" />
                <AlertDescription className="!text-red-800 dark:!text-red-200 font-semibold ml-2">{t('adminBillingView.expired')}</AlertDescription>
              </Alert>
            ) : (
              <Alert className="w-fit px-3 py-2 bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700 text-green-800 dark:text-green-200">
                <CheckCircle className="w-4 h-4 text-green-700 dark:text-green-400" />
                <AlertDescription className="!text-green-800 dark:!text-green-200 font-semibold">{t('adminBillingView.active')}</AlertDescription>
              </Alert>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('adminBillingView.currentPlan')}</div>
              <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                {plan?.planName.split(' ').slice(0, 2).join(' ')}
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('adminBillingView.teamSize')}</div>
              <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                {pluralize('member', plan?.seatCount ?? 1, true)}
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('adminBillingView.billingPeriod')}</div>
              <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                {t('adminBillingView.trial')}
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('adminBillingView.amount')}</div>
              <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                ${plan?.price}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
            <Button
              variant="outline"
              onClick={() => router.navigate('/account-subscription', router.query)}
              className="w-full text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              {t('adminBillingView.changePlan')}
            </Button>
            <Button
              variant="outline"
              onClick={() => router.navigate('/account-subscription', router.query)}
              className="w-full text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              {t('adminBillingView.editTeamSize')}
            </Button>
            <div className="w-full flex justify-center">
              <TrialProgressBar />
            </div>
            <div className="w-full">
              {displaySubscription?.isActive ? (
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {displaySubscription?.isLive ? t('adminBillingView.renewalTime') : t('adminBillingView.expires')}:
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {displaySubscription?.renewalTime?.format('MMM DD, YYYY')}
                  </div>
                </div>
              ) : (
                <Alert className="bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700 !text-red-800 dark:!text-red-200" variant="destructive">
                  <XCircle className="w-4 h-4 text-red-700 dark:text-red-400" />
                  <AlertDescription className="!text-red-800 dark:!text-red-200 font-medium ml-2">{t('adminBillingView.expired')}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-start">
            {displaySubscription?.isLive ? (
              <BTButton
                loading={subscriptionCancelling}
                onClick={() => {
                  callCancelSubscription()
                }}
                btvariant="text"
              >
                {t('adminBillingView.cancelSubscription')}
              </BTButton>
            ) : (
              <Alert className="bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700 !text-red-800 dark:!text-red-200" variant="destructive">
                <XCircle className="w-4 h-4 text-red-700 dark:text-red-400" />
                <AlertDescription className="!text-red-800 dark:!text-red-200 font-medium ml-2">{t('adminBillingView.cancelled')}</AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {paymentInfoBox()}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('adminBillingView.invoices')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400">
                {t('adminBillingView.viewDownloadInvoices')}
              </p>
              <BTButton
                btvariant="secondary"
                onClick={() => {
                  toggleDrawer("Invoices");
                }}
              >
                {t('adminBillingView.viewInvoices')}
              </BTButton>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('adminBillingView.invoiceDetails')}</CardTitle>
          </CardHeader>
          <CardContent>
            {!shopperContactInfo ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('adminBillingView.companyName')}</div>
                    <div className="text-gray-900 dark:text-gray-100">{companyName || 'Not specified'}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('adminBillingView.fullName')}</div>
                    <div className="text-gray-900 dark:text-gray-100">{fullName}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('adminBillingView.address')}</div>
                    <div className="text-gray-900 dark:text-gray-100">{fullAddress || 'Not specified'}</div>
                  </div>
                </div>
                <BTButton
                  btvariant="secondary"
                  onClick={() => {
                    toggleDrawer("Invoice details")
                  }}
                >
                  {t('adminBillingView.editInvoiceDetails')}
                </BTButton>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )



  return (
    <div className={classes.pageContainer}>
      <div className={classes.titleContainer}>
        <h4 className={classes.pageTitle}>{t('adminBillingView.viewTitle')}</h4>
      </div>
      <div
        style={{
          display: "flex",

          position: "relative",
        }}
        className={classes.accountSettingBody}
      >

        {!isLoading && billingViewMode === 'billing' && buildBillingView()}
        {!isLoading && billingViewMode === 'noSubscription' && buildNoSubscriptionView()}
        {!isLoading && billingViewMode === 'trial' && buildTrialView()}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        )}

        {drawerStatus &&
          <BTDrawer
            anchor="right"
            open={drawerStatus}
            onClose={toggleDrawer}
            sx={{
              width: drawerView === "Invoices" ? "" : "320px",
              ".MuiPaper-root": {
                overflow: "hidden"
              },

            }}
          >
            <div
              style={{
                width: drawerView === "Invoices" ? "750px" : "320px",
                overflow: "auto",
                height: "100%",
              }}
              className={classes.billingOpenModal}
            >
              <Button
                variant="ghost"
                size="sm"
                className="p-0 h-auto text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => {
                  toggleDrawer("");
                }}
              >
                <img alt='' src={closeBillingModal} width={16} height={16} />
              </Button>

              <h3 className={classes.modalTitle}>{drawerView}</h3>

              {drawerView === "Invoices" &&
                <InvoiceTable />
              }
              {drawerView === "Invoice details" &&
                <EditContactDetailsForm initialValue={invoiceDetails} onSubmit={handleSubmit} />
              }
            </div>
          </BTDrawer>

        }
        <ReusableModal />
      </div >
    </div >
  );
};

export default AdminBillingView;
